import { NgClass } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { BreadcrumbModel } from '@core/models/shared';
import { TranslateService } from '@ngx-translate/core';
import { filter, startWith, Subscription } from 'rxjs';

@Component({
  selector: 'app-breadcrumb',
  imports: [RouterLink, NgClass],
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.scss'
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  readonly translateService = inject(TranslateService);

  // Definizione di un segnale (WritableSignal) per mantenere lo stato della breadcrumb
  breadcrumbs: BreadcrumbModel[] = [];

  // Iniezione del Router per ascoltare eventi di navigazione
  router: Router = inject(Router);

  // Iniezione dell'ActivatedRoute per ottenere informazioni sulla route attiva
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);

  subscriptions: Subscription[] = [];

  ngOnInit(): void {
    this.subscriptions.push(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd), startWith(null)) // Filtra solo gli eventi di fine navigazione
        .subscribe(() => {
          this.breadcrumbs = this._buildBreadcrumbs(); // Aggiorna la breadcrumb quando cambia la navigazione
        })
    )
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Questo metodo costruisce dinamicamente un array di breadcrumb basato sulla struttura delle route attive.
   * Tiene conto di eventuali parametri dinamici numerici nelle rotte.
   * @returns {BreadcrumbModel[]} - Ritorna un array di oggetti breadcrumb con nome e route associata.
   */
  private _buildBreadcrumbs(): BreadcrumbModel[] {
    let route = this.activatedRoute.root; // Ottiene la route radice
    let breadcrumbData: any[] = []; // Array per memorizzare le informazioni breadcrumb definite nelle route

    // Naviga attraverso le route figlie per trovare le informazioni della breadcrumb
    while (route.firstChild) {
      route = route.firstChild;
      if (route.snapshot.data['breadcrumb']) {
        breadcrumbData = route.snapshot.data['breadcrumb']; // Estrae i dati breadcrumb dalla route attuale
      }
    }

    // Suddivide l'URL corrente in segmenti rimuovendo eventuali stringhe vuote
    let pathSegments = this.router.url.split('/').filter(segment => segment);
    let accumulatedPath = '';

    // Costruisce dinamicamente la breadcrumb accumulando progressivamente il percorso
    return breadcrumbData.reduce<{ name: string; route: string }[]>((acc, item) => {
      // Trova l'indice del segmento corrispondente nella URL
      let segmentIndex = pathSegments.indexOf(item.route);
      if (segmentIndex !== -1) {
        accumulatedPath += `/${pathSegments[segmentIndex]}`; // Aggiunge il segmento al percorso accumulato

        // Se il segmento successivo è un numero, lo aggiunge al percorso
        let nextSegment = pathSegments[segmentIndex + 1];
        if (nextSegment && !isNaN(Number(nextSegment))) {
          accumulatedPath += `/${nextSegment}`;
        }
      }

      // Aggiunge la breadcrumb aggiornata all'array, rimuovendo lo slash iniziale
      acc.push({ ...item, route: accumulatedPath.slice(1) });

      const breadcrumbTranslated: BreadcrumbModel[] = acc.map((breadcrumb) => {
        return { ...breadcrumb, name: this.translateService.instant(breadcrumb.name) };
      });

      return breadcrumbTranslated;
    }, []);
  }
}